import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock3, Plus, RefreshCw, X } from "lucide-react";
import { ConversationMark } from "../OlliLogo";
import { PageLoader } from "../PageLoader";
import { OlliSendButton } from "./OlliSendButton";
import { OlliThreadView, ThreadHistory } from "./OlliThreadView";
import { logPrompt } from "./RecentPrompts";
import { useFacility } from "../../../context/FacilityContext";
import { askOlliPath, deleteOlliChat, getOlliChat, listOlliChats, olliErrorOf, sendToOlli } from "../../lib/olli";

/**
 * The floating Olli assistant from the design (components/assistant/OlliAssistant.tsx).
 *
 * Talks to /api/olli (item 1, see lib/olli.js), like the Ask Olli page and
 * with the same thread: answers with steps, facts, sources, deep links and
 * follow-up chips; a failed turn shows why with Retry (the backend saves
 * nothing for it). Lists, opens and deletes past chats, and "Open in Ask Olli"
 * continues the chat on the full page. The Home box opens it with a prompt
 * ("olli:ask" event, item 130); the phone top bar opens it with none.
 * The round launcher is hidden on the Ask Olli page (it is the same assistant)
 * and below 640px, where the top bar carries the Olli button instead, so it
 * never sits on a reply box.
 */

const SUGGESTIONS = ["Show leads this week", "Who's coming in tomorrow?", "Any open complaints?", "What plan am I on?", "Add a lead: Priya Sharma, 9876543210, implants"];

function greetingName() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return (user?.name || "").trim();
  } catch (_) {
    return "";
  }
}

export default function OlliAssistant() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onAskOlliPage = pathname.startsWith("/ask-olli");
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const [draftText, setDraftText] = useState("");
  const textareaRef = useRef(null);
  const name = greetingName();
  const fac = useFacility();
  const facilityId = fac?.apiParams?.().facility_id || null;
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [typing, setTyping] = useState(false);
  const [chats, setChats] = useState(null);
  const [chatsError, setChatsError] = useState("");
  // The chat being opened from the list: the loader shows until it arrives, and a
  // late answer for a chat no longer being opened is dropped.
  const [opening, setOpening] = useState(null);
  const openingRef = useRef(null);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "j") { event.preventDefault(); setOpen((v) => !v); }
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // The Ask Olli page is the same assistant: the panel closes when it opens.
  useEffect(() => { if (onAskOlliPage) setOpen(false); }, [onAskOlliPage]);
  useEffect(() => { const textarea = textareaRef.current; if (!textarea) return; textarea.style.height = "auto"; textarea.style.height = `${Math.min(textarea.scrollHeight, 112)}px`; }, [draftText]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [messages, typing]);
  const loadChats = () => {
    setChats(null);
    setChatsError("");
    listOlliChats().then(setChats).catch((e) => { setChats([]); setChatsError(e.response?.data?.message || "Your chats couldn't be loaded."); });
  };
  useEffect(() => { if (history) loadChats(); }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (text, chat = chatId, surface = null) => {
    const message = String(text || "").trim();
    if (!message || typing) return;
    setError("");
    setDraftText("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text: message }]);
    setTyping(true);
    try {
      const r = await sendToOlli({ message, chatId: chat, facilityId });
      const id = r.chatId || chat;
      setChatId(id);
      setMessages((m) => [...m, r.reply]);
      // The prompt box it came from can now open this chat ("Open chat").
      if (surface && id) logPrompt(surface, message, id);
    } catch (e) {
      // A chat deleted elsewhere: the retry starts a new one.
      if (e.response?.status === 404 && chat) setChatId(null);
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "olli", text: "", error: olliErrorOf(e), retry: message }]);
    } finally {
      setTyping(false);
    }
  };
  // Retry: drop the failed question and its error, then ask again (nothing of it was saved).
  const retry = (failed) => {
    setMessages((m) => {
      const at = m.findIndex((x) => x.id === failed.id);
      return at > 0 && m[at - 1].role === "user" ? [...m.slice(0, at - 1), ...m.slice(at + 1)] : m.filter((x) => x.id !== failed.id);
    });
    send(failed.retry);
  };
  const newChat = () => { openingRef.current = null; setOpening(null); setHistory(false); setDraftText(""); setMessages([]); setChatId(null); setError(""); };
  const openChat = async (id) => {
    openingRef.current = id;
    setOpening(id);
    setError("");
    try { const thread = await getOlliChat(id); if (openingRef.current !== id) return; setMessages(thread); setChatId(id); setHistory(false); } catch (e) { if (openingRef.current !== id) return; setChatsError(e.response?.data?.message || "That chat couldn't be opened."); }
    openingRef.current = null;
    setOpening(null);
  };
  const removeChat = async (id) => {
    try {
      await deleteOlliChat(id);
      setChats((c) => (c || []).filter((x) => x.id !== id));
      if (id === chatId) newChat();
    } catch (e) {
      setChatsError(e.response?.data?.message || "That chat couldn't be deleted.");
    }
  };
  // The Home box (and anything else) opens Olli with a prompt; no prompt just opens it.
  useEffect(() => {
    const onAsk = (event) => { const prompt = event.detail?.prompt; setOpen(true); setHistory(false); if (prompt) { setMessages([]); setChatId(null); send(prompt, null, event.detail?.surface || null); } };
    window.addEventListener("olli:ask", onAsk);
    return () => window.removeEventListener("olli:ask", onAsk);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = (event) => { event.preventDefault(); send(draftText); };
  const openInAskOlli = chatId && !typing ? () => { setOpen(false); navigate(askOlliPath(chatId)); } : undefined;

  return <>
    {!onAskOlliPage && <div className="group fixed bottom-6 right-6 z-40 max-sm:hidden">
      {!open && <span className="pointer-events-none absolute -left-20 top-1/2 -translate-y-1/2 rounded-sm bg-ink px-3 py-1.5 text-caption font-medium text-paper opacity-0 transition-opacity group-hover:opacity-100">Ask Olli</span>}
      <button type="button" aria-label={open ? "Close Olli assistant" : "Ask Olli"} title="Ask Olli" onClick={() => setOpen((v) => !v)} className="flex h-14 w-14 items-center justify-center rounded-full bg-ink shadow-lift ring-1 ring-paper/30 transition-transform duration-200 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-deep"><ConversationMark tone="black" className="h-9 w-9" /></button>
    </div>}
    <section aria-label="Olli assistant" className={`fixed bottom-24 right-6 z-40 flex h-[640px] max-h-[calc(100vh-120px)] w-[420px] max-w-[calc(100vw-32px)] origin-bottom-right flex-col overflow-hidden rounded-xl bg-paper shadow-lift ring-1 ring-line transition-[opacity,transform] duration-200 max-sm:inset-x-2 max-sm:bottom-2 max-sm:top-16 max-sm:h-auto max-sm:max-h-none max-sm:w-auto max-sm:max-w-none max-sm:origin-top-right ${open ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"}`}>
      <header className="flex h-[68px] shrink-0 items-center gap-2 border-b border-line px-4 sm:gap-3">
        {history ? <button type="button" aria-label="Back to chat" onClick={() => setHistory(false)} className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-mist"><ChevronLeft size={18} strokeWidth={1.75} /></button> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink"><ConversationMark tone="black" className="h-7 w-7" /></span>}
        <div className="min-w-0 flex-1"><p className="truncate text-body font-semibold leading-tight">{history ? "Your chats" : "Olli"}</p><p className="truncate text-caption text-muted">{history ? "Previous conversations" : "Works across your clinic"}</p></div>
        <button type="button" aria-label="Chats" title="Chats" onClick={() => setHistory((value) => !value)} className="flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-caption font-semibold text-muted hover:bg-mist hover:text-ink"><Clock3 size={15} strokeWidth={1.75} />Chats</button>
        <button type="button" aria-label="New chat" title="New chat" onClick={newChat} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><Plus size={17} strokeWidth={1.75} /></button>
        <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><X size={18} strokeWidth={1.75} /></button>
      </header>
      {history ? <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {chatsError && <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-danger-soft px-3 py-2 text-caption text-danger"><span>{chatsError}</span><button type="button" onClick={loadChats} className="inline-flex shrink-0 items-center gap-1 font-semibold text-ink hover:underline"><RefreshCw size={13} strokeWidth={1.75} />Try again</button></div>}
        {chats === null || opening ? <PageLoader label={opening ? "Loading chat…" : "Loading chats…"} className="py-10" /> : chats.length ? <ThreadHistory threads={chats} onSelect={openChat} onDelete={removeChat} /> : !chatsError && <p className="py-6 text-center text-small text-muted">No chats yet.</p>}
      </div> : <>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {messages.length ? <div><OlliThreadView messages={messages} typing={typing} onChip={(chip) => send(chip)} onRetry={retry} onOpenAssistant={openInAskOlli} />{error && <p role="alert" className="mt-3 text-caption text-danger">{error}</p>}<div ref={bottomRef} /></div> : <div className="flex min-h-full flex-col justify-center gap-5"><div className="flex items-start gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink"><ConversationMark tone="black" className="h-6 w-6" /></span><div><p className="pt-1 text-body font-medium">{name ? `Hi ${name}. What should I do?` : "What should I do?"}</p><p className="mt-1 text-caption text-muted">Ask about your clinic's numbers, search your documents or fill in a new lead.</p></div></div><div className="flex flex-wrap gap-2">{SUGGESTIONS.map((suggestion) => <button key={suggestion} type="button" onClick={() => send(suggestion)} className="rounded-lg bg-mist px-3 py-2 text-left text-caption text-ink transition-colors hover:bg-sky-soft">{suggestion}</button>)}</div>{error && <p role="alert" className="text-caption text-danger">{error}</p>}</div>}
        </div>
        <form onSubmit={submit} className="shrink-0 border-t border-line px-4 pb-3 pt-3"><div className="flex items-end gap-2 rounded-lg border border-line bg-paper p-2 focus-within:border-sky-deep focus-within:ring-2 focus-within:ring-sky/30"><textarea ref={textareaRef} value={draftText} onChange={(event) => setDraftText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(draftText); } }} rows={1} maxLength={2000} placeholder="Ask Olli to help with your clinic" aria-label="Message Olli" className="max-h-28 min-h-8 flex-1 resize-none bg-transparent px-1 py-1 text-small outline-none placeholder:text-muted" /><OlliSendButton size="sm" disabled={!draftText.trim() || typing} sending={typing} aria-label="Send message" /></div><p className="pt-2 text-center text-caption text-muted">Olli can make mistakes. Check before you confirm.</p></form>
      </>}
    </section>
  </>;
}
